import { FullScreenLoader } from '@ui/components/FullScreenLoader';
import { AdminLayout } from '@ui/components/layout/AdminLayout';
import { HomeLayout } from '@ui/components/layout/HomeLayout';
import { MetricsLayout } from '@ui/components/layout/MetricsLayout';
import { SiteLayout } from '@ui/components/layout/SiteLayout';
import { SiteLayoutPending } from '@ui/components/layout/SiteLayout/SiteLayoutPending';
import { UserLayout } from '@ui/components/layout/UserLayout';
import { WithPermission } from '@ui/components/Permission';
import { ProtectedRoute } from '@ui/components/ProtectedRoute';
import { WithCurrentSite } from '@ui/components/WithCurrentSite';
import { WithCurrentTeam } from '@ui/components/WithCurrentTeam';
import { SnapSiteConfigEdit } from '@ui/features/snap-site-config/screens/edit/SnapSiteConfigEdit';
import NotFound from '@ui/pages/NotFound';
import React from 'react';
import { Navigate, Outlet, Route } from 'react-router';

import { routesConfig } from './config';
import { path } from './path-helpers';

const Home = React.lazy(() => import('../../pages/Home'));
const QuoteQueue = React.lazy(() => import('@ui/pages/QuoteQueue'));
const AdminMetrics = React.lazy(
  () => import('@ui/pages/Admin/Metrics/AdminMetrics'),
);
const AdminSiteIndex = React.lazy(
  () => import('@ui/pages/Admin/Site/AdminSiteIndex'),
);
const AdminUserIndex = React.lazy(
  () => import('@ui/pages/Admin/User/AdminUserIndex'),
);
const LoggedOut = React.lazy(() => import('@ui/pages/LoggedOut'));
const Profile = React.lazy(() => import('@ui/pages/Profile'));
const QuoteView = React.lazy(() => import('@ui/pages/QuoteView/QuoteView'));
const SiteShow = React.lazy(() => import('@ui/pages/SiteShow'));
const DuplicateExistingQuote = React.lazy(
  () => import('@ui/pages/SnapQuoteDuplicate'),
);
const AutoConfigurator = React.lazy(
  () => import('@ui/features/alpha/components/AutoConfigurator'),
);
const SnapQuoteEdit = React.lazy(() => import('@ui/pages/SnapQuoteEdit'));
const SnapQuoteNew = React.lazy(() => import('@ui/pages/SnapQuoteNew'));
const SnapQuoteView = React.lazy(() => import('@ui/pages/SnapQuoteView'));
const PartSearch = React.lazy(() => import('@ui/pages/PartSearch'));

export function AppRoutes() {
  const {
    root,
    quoteQueue,
    quoteView,
    duplicateExistingQuote,
    snapQuoteAutoConfiguratorUI,
    snapQuoteEdit,
    snapQuoteNew,
    snapQuoteView,
    userProfile,
    metrics,
    admin,
    adminUsers,
    adminSites,
    adminSnapSiteConfig,
    partSearch,
  } = routesConfig;

  return (
    <>
      {/* Authenticated routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        }
      >
        {/* Homepage */}
        <Route element={<HomeLayout />}>
          <Route index element={<Home />} />
        </Route>

        {/* Quote redirects */}
        <Route path="/quotes" element={<Navigate to={path(root)} />} />
        <Route path="/quote/new" element={<Navigate to={path(root)} />} />

        {/* User routes */}
        <Route path={userProfile.path} element={<UserLayout />}>
          <Route index element={<Profile />} />
        </Route>

        {/* Site routes */}
        <Route
          path="/teams/:teamCode"
          element={
            <WithCurrentTeam fallback={<SiteLayoutPending />}>
              <Outlet />
            </WithCurrentTeam>
          }
        >
          <Route element={<SiteLayout />}>
            <Route index element={<SiteShow />} />

            {/* Root Quotes */}
            <Route path={quoteQueue.path} element={<QuoteQueue />} />

            {/* Visual Quotes */}
            <Route path={quoteView.path} element={<QuoteView />} />

            {/* AutoConfigurator UI */}
            <Route
              path={snapQuoteAutoConfiguratorUI.path}
              element={<AutoConfigurator />}
            />

            {/* Snap Quotes */}
            <Route
              path={duplicateExistingQuote.path}
              element={<DuplicateExistingQuote />}
            />
            <Route path={snapQuoteEdit.path} element={<SnapQuoteEdit />} />
            <Route path={snapQuoteNew.path} element={<SnapQuoteNew />} />
            <Route path={snapQuoteView.path} element={<SnapQuoteView />} />

            {/* Search */}
            <Route path={partSearch.path} element={<PartSearch />} />
          </Route>
        </Route>

        <Route element={<MetricsLayout />}>
          <Route path={metrics.path} element={<AdminMetrics />} />
        </Route>

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <WithPermission minimumRole={admin.minimumRole} showUnauthorized>
              <AdminLayout />
            </WithPermission>
          }
        >
          <Route index element={<Navigate to={adminSites.path} replace />} />

          <Route
            path={adminSnapSiteConfig.path}
            element={
              <WithPermission
                minimumRole={adminSnapSiteConfig.minimumRole}
                showUnauthorized
              >
                <WithCurrentSite fallback={<FullScreenLoader />}>
                  <SnapSiteConfigEdit />
                </WithCurrentSite>
              </WithPermission>
            }
          />

          <Route
            path={adminUsers.path}
            element={
              <WithPermission
                minimumRole={adminUsers.minimumRole}
                showUnauthorized
              >
                <AdminUserIndex />
              </WithPermission>
            }
          />

          <Route
            path={adminSites.path}
            element={
              <WithPermission
                minimumRole={adminSites.minimumRole}
                showUnauthorized
              >
                <AdminSiteIndex />
              </WithPermission>
            }
          />
        </Route>
      </Route>

      {/* User auth routes */}
      <Route path="/auth/redirect" element={null} />
      <Route path="/logout/redirect" element={<LoggedOut />} />

      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </>
  );
}
